import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HardDrive, Info, Pencil, TreePine, Trash2, Users } from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { TreeFormModal } from '@/components/Layout/TreeFormModal';
import {
  listTrees,
  deleteTree,
  fetchTreeQuota,
  fetchSpeicherstand,
  setzeWappen,
  loescheWappen,
  type Personenstand,
  type Speicherstand,
  type Tree,
} from '@/api/trees.api';
import { Wappen } from '@/components/Layout/Wappen';
import { useTreeContextStore } from '@/store/useTreeContextStore';
import { groesse } from '@/utils/format';

// ─── Speicher des Archivs ───────────────────────────────────────────────────
//
// Die Grenze kommt aus dem Tarif des BESITZERS, nicht aus dem eigenen. Für
// Mitglieder ist das der wesentliche Punkt: Sie sehen hier, wie viel in diesem
// Stammbaum noch frei ist – nicht, was Ihr eigener Tarif hergäbe.

/** Ein Fortschrittsbalken, der ab 75 % gelb und ab 90 % rot wird. */
function Balken({ prozent }: { prozent: number }) {
  const voll = prozent >= 90;
  const knapp = prozent >= 75 && !voll;
  return (
    <div
      className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"
      role="progressbar"
      aria-valuenow={prozent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={`h-full rounded-full transition-all ${
          voll ? 'bg-red-500' : knapp ? 'bg-amber-500' : 'bg-emerald-500'
        }`}
        style={{ width: `${Math.max(prozent, 1)}%` }}
      />
    </div>
  );
}

function PersonenKarte({ stand, tarif }: { stand: Personenstand; tarif: string }) {
  if (stand.grenze === null) return null;
  const voll = stand.prozent !== null && stand.prozent >= 90;

  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Users className="h-4 w-4 text-slate-400" aria-hidden="true" />
        Personen in diesem Stammbaum
      </h2>
      <p className="mt-2 text-sm text-slate-600">
        <span className="font-medium text-slate-900">{stand.anzahl}</span> von {stand.grenze}
        {stand.frei !== null && ` · noch ${stand.frei} frei`}
      </p>
      <Balken prozent={stand.prozent ?? 0} />
      <p className="mt-2 text-xs text-slate-400">
        Grenze aus dem Tarif <span className="font-medium">{tarif}</span>, gilt je
        Stammbaum. Für mehr wenden Sie sich an den Betreiber.
      </p>
      {voll && (
        <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">
          Fast voll – weitere Personen werden abgewiesen.
        </p>
      )}
    </section>
  );
}

function SpeicherKarte({ stand }: { stand: Speicherstand }) {
  const voll = stand.prozent !== null && stand.prozent >= 90;
  const mehrereBaeume = stand.baeume > 1;

  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <HardDrive className="h-4 w-4 text-slate-400" aria-hidden="true" />
        Speicher im Archiv
      </h2>

      {stand.grenzeBytes === null ? (
        <p className="mt-2 text-sm text-slate-600">
          {groesse(stand.belegtBytes)} belegt. Der Tarif{' '}
          <span className="font-medium">{stand.tarif}</span> setzt keine Grenze.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-slate-600">
            <span className="font-medium text-slate-900">
              {groesse(stand.belegtGesamtBytes)}
            </span>{' '}
            von {groesse(stand.grenzeBytes)} belegt
            {stand.freiBytes !== null && ` · ${groesse(stand.freiBytes)} frei`}
          </p>
          {/* Bei mehreren Bäumen ist die Summe die Grenze – aber die Frage
              „wie viel davon liegt HIER?" beantwortet sie nicht. */}
          {mehrereBaeume && (
            <p className="mt-0.5 text-xs text-slate-500">
              davon {groesse(stand.belegtBytes)} in diesem Stammbaum · Summe über{' '}
              {stand.baeume} Stammbäume
            </p>
          )}

          <Balken prozent={stand.prozent ?? 0} />

          <p className="mt-2 text-xs text-slate-400">
            Grenze aus dem Tarif <span className="font-medium">{stand.tarif}</span>, gilt
            für alle Ihre Stammbäume zusammen. Für mehr Platz wenden Sie sich an den
            Betreiber.
          </p>

          {voll && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">
              Fast voll – weitere Dateien werden abgewiesen. Löschen Sie etwas im
              Archiv, oder lassen Sie den Tarif erweitern.
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ─── Der Stammbaum selbst ───────────────────────────────────────────────────
//
// Name, Umfang, Kontingent – und der Weg, ihn wieder loszuwerden. Alles, was
// den Stammbaum als Ganzes betrifft; wer darin mitarbeitet, steht unter
// „Mitglieder".

// ─── Familienwappen ─────────────────────────────────────────────────────────
//
// Das Bild, an dem man seinen Stammbaum wiedererkennt – im Umschalter und in
// der Kopfzeile. Wer mehrere Bäume führt, unterscheidet sie sonst nur am
// Namen, und Namen ähneln sich in Familien bekanntlich.

function WappenKarte({ baum }: { baum: Tree }) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const dateiFeld = useRef<HTMLInputElement>(null);

  const neuLaden = () => queryClient.invalidateQueries({ queryKey: ['trees'] });

  const hochladen = useMutation({
    mutationFn: (datei: File) => setzeWappen(baum.id, datei),
    onSuccess: () => {
      neuLaden();
      toast.success('Wappen übernommen.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const entfernen = useMutation({
    mutationFn: () => loescheWappen(baum.id),
    onSuccess: () => {
      neuLaden();
      toast.success('Wappen entfernt.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const waehlen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const datei = e.target.files?.[0];
    // Das Feld zurücksetzen: sonst löst dieselbe Datei beim zweiten Mal kein
    // change-Ereignis aus, und der Nutzer hält es für kaputt.
    e.target.value = '';
    if (datei) hochladen.mutate(datei);
  };

  const wegnehmen = async () => {
    const ok = await confirm({
      title: 'Wappen entfernen?',
      message: (
        <>
          Der Stammbaum zeigt danach wieder das allgemeine Symbol. Das Bild wird
          gelöscht.
        </>
      ),
      confirmLabel: 'Entfernen',
      variant: 'danger',
    });
    if (ok) entfernen.mutate();
  };

  const laeuft = hochladen.isPending || entfernen.isPending;

  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-slate-900">Familienwappen</h2>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        Ein Bild, das für diesen Stammbaum steht – im Umschalter und in der
        Kopfzeile. PNG, JPEG, WebP oder GIF; es wird auf 512 Pixel verkleinert.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
          <Wappen url={baum.wappenUrl} groesse={16} />
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={dateiFeld}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={waehlen}
            className="hidden"
          />
          <button
            onClick={() => dateiFeld.current?.click()}
            disabled={laeuft}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {hochladen.isPending
              ? 'Wird übernommen…'
              : baum.wappenUrl
                ? 'Anderes Bild wählen'
                : 'Wappen hochladen'}
          </button>
          {baum.wappenUrl && (
            <button
              onClick={wegnehmen}
              disabled={laeuft}
              className="rounded-md px-3 py-2 text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
            >
              Entfernen
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export function TreeSettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { activeTreeId, clearActiveTree } = useTreeContextStore();
  const [zeigeFormular, setZeigeFormular] = useState(false);

  const { data: trees, isLoading } = useQuery({ queryKey: ['trees', 'accessible'], queryFn: listTrees });
  const { data: kontingent } = useQuery({ queryKey: ['trees', 'quota'], queryFn: fetchTreeQuota });
  const { data: speicher } = useQuery({
    queryKey: ['trees', 'storage', activeTreeId],
    queryFn: () => fetchSpeicherstand(activeTreeId!),
    enabled: Boolean(activeTreeId),
  });

  const baum = trees?.find((t) => t.id === activeTreeId);

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteTree(id),
    onSuccess: () => {
      // Die gemerkte Auswahl zeigt ins Leere – verwerfen, damit der TreeGuard
      // neu wählt (oder das leere Gerüst mit „Anlegen" zeigt).
      clearActiveTree();
      queryClient.invalidateQueries({ queryKey: ['trees'] });
      toast.success('Stammbaum gelöscht.');
      navigate('/');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const jetztLoeschen = async () => {
    if (!baum) return;
    const ok = await confirm({
      title: 'Stammbaum löschen?',
      message: (
        <>
          <strong>„{baum.name}"</strong> wird mit{' '}
          <strong>
            allen {baum.personCount} {baum.personCount === 1 ? 'Person' : 'Personen'}
          </strong>
          , Beziehungen, Medien und Quellen unwiderruflich gelöscht. Auch für alle
          anderen Mitglieder. Laden Sie vorher eine Sicherung herunter, wenn Sie die
          Daten behalten möchten.
        </>
      ),
      confirmLabel: 'Endgültig löschen',
      variant: 'danger',
    });
    if (ok) loeschen.mutate(baum.id);
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16">
          <LoadingSpinner message="Stammbaum wird geladen..." />
        </div>
      </AppLayout>
    );
  }

  // Kein Besitzer: verwalten kann man hier nichts. Der Speicherstand bleibt
  // trotzdem sichtbar – wer hochlädt, soll wissen, was noch hineinpasst.
  if (!baum?.isOwner) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-lg px-4 py-12 sm:px-6">
          <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
            <div className="text-sm leading-relaxed text-slate-600">
              <h1 className="text-base font-semibold text-slate-900">
                Dieser Stammbaum gehört jemand anderem
              </h1>
              <p className="mt-2">
                Name, Beschreibung und Bestand ändert die Person, der er gehört.
                Ihre Arbeit darin ist davon nicht berührt.
              </p>
            </div>
          </div>
          {speicher && <SpeicherKarte stand={speicher} />}
          {speicher && <PersonenKarte stand={speicher.personen} tarif={speicher.tarif} />}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <TreePine className="h-6 w-6 text-emerald-600" aria-hidden="true" />
            Stammbaum
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Ihr Stammbaum als Ganzes. Wer daran mitarbeitet, steht unter Mitglieder.
          </p>
        </header>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900">{baum.name}</h2>
              {baum.description ? (
                <p className="mt-1 text-sm text-slate-600">{baum.description}</p>
              ) : (
                <p className="mt-1 text-sm text-slate-400">Keine Beschreibung</p>
              )}
              <p className="mt-2 text-xs text-slate-400">
                {baum.personCount} {baum.personCount === 1 ? 'Person' : 'Personen'} · angelegt am{' '}
                {new Date(baum.createdAt).toLocaleDateString('de-DE')}
              </p>
            </div>
            <button
              onClick={() => setZeigeFormular(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Bearbeiten
            </button>
          </div>

          <button
            onClick={() => navigate('/members')}
            className="mt-4 flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
          >
            <Users className="h-4 w-4" aria-hidden="true" />
            Mitglieder verwalten
          </button>
        </section>

        <WappenKarte baum={baum} />

        {speicher && <SpeicherKarte stand={speicher} />}
        {speicher && <PersonenKarte stand={speicher.personen} tarif={speicher.tarif} />}

        {kontingent && !kontingent.unbegrenzt && (
          <p className="mt-4 text-sm text-slate-500">
            Sie besitzen {kontingent.used} von {kontingent.limit} möglichen Stammbäumen
            {kontingent.individuell ? ' (eigene Vereinbarung)' : ` im Tarif ${kontingent.tarif}`}.
            {kontingent.remaining === 0 &&
              ' Für weitere wenden Sie sich an den Betreiber.'}
          </p>
        )}

        {/* ─── Löschen ────────────────────────────────────────────────── */}
        <section className="mt-10 rounded-xl border border-red-200 bg-red-50/50 p-5">
          <h2 className="text-sm font-semibold text-red-900">Stammbaum löschen</h2>
          <p className="mt-1 text-sm leading-relaxed text-red-800">
            Entfernt den Stammbaum mit allen Personen, Beziehungen, Medien und
            Quellen – auch für alle anderen Mitglieder. Das lässt sich nicht
            rückgängig machen.
          </p>
          <button
            onClick={jetztLoeschen}
            disabled={loeschen.isPending}
            className="mt-3 flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {loeschen.isPending ? 'Wird gelöscht…' : 'Stammbaum löschen'}
          </button>
        </section>
      </div>

      {zeigeFormular && <TreeFormModal tree={baum} onClose={() => setZeigeFormular(false)} />}
    </AppLayout>
  );
}

export default TreeSettingsPage;
